/**
 * Time Tracking Activity Log Types
 * Types for the real-time activity feed in the Time Tracking tab
 */

export type TimeActivityEventType =
  | 'clock_arrival'
  | 'clock_departure'
  | 'clock_break_start'
  | 'clock_break_end'
  | 'manual_edit'
  | 'manual_delete'
  | 'overtime_alert'
  | 'geofence_violation'
  | 'shift_auto_close'
  | 'shift_started'
  | 'shift_ended'
  | 'employee_no_show'
  | 'unassigned_shift_started'
  | 'unassigned_shift_ended';

export interface TimeTrackingActivityMetadata {
  // Clock events
  auth_method?: 'qr' | 'pin' | 'manual' | 'dotypos';
  device_id?: string;
  source?: 'internal' | 'dotypos';
  location?: { lat: number; lng: number };

  // Manual edits
  previous_time?: string;
  new_time?: string;
  field?: 'arrival' | 'departure';

  // Overtime alerts
  hours_worked?: number;
  threshold_hours?: number;
  overtime_minutes?: number;

  // Geofence violations
  distance_meters?: number;
  allowed_radius?: number;

  // Shift lifecycle events
  shift_title?: string;
  shift_start_time?: string;
  shift_end_time?: string;
  employee_name?: string;
  grace_minutes?: number;
}

export interface TimeTrackingActivityEvent {
  id: string;
  business_id: string;
  employee_id: string | null; // Nullable for unassigned shift events
  event_type: TimeActivityEventType;
  event_timestamp: string;
  created_at: string;
  time_log_id: string | null;
  shift_id: string | null;
  actor_user_id: string | null;
  actor_employee_id: string | null;
  metadata: TimeTrackingActivityMetadata;
  description: string | null;
}

// Extended type with joined employee data
export interface TimeTrackingActivityEventWithEmployee extends TimeTrackingActivityEvent {
  employee: {
    id: string;
    name: string;
    avatar_url?: string | null;
  } | null;
  actor_employee?: {
    id: string;
    name: string;
  } | null;
}

export interface TimeTrackingActivityFilters {
  dateRange?: { start: Date; end: Date };
  employeeIds?: string[];
  eventTypes?: TimeActivityEventType[];
}

// Event display configuration
export interface ActivityEventConfig {
  icon: string;
  color: string;
  bgColor: string;
  dotColor: string; // Solid color for small dot indicators
  label: string;
}

export const ACTIVITY_EVENT_CONFIG: Record<TimeActivityEventType, ActivityEventConfig> = {
  clock_arrival: {
    icon: 'LogIn',
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    dotColor: 'bg-green-500',
    label: 'Clocked In',
  },
  clock_departure: {
    icon: 'LogOut',
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    dotColor: 'bg-blue-500',
    label: 'Clocked Out',
  },
  clock_break_start: {
    icon: 'Coffee',
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    dotColor: 'bg-orange-500',
    label: 'Break Started',
  },
  clock_break_end: {
    icon: 'Play',
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    dotColor: 'bg-emerald-500',
    label: 'Break Ended',
  },
  manual_edit: {
    icon: 'Pencil',
    color: 'text-primary',
    bgColor: 'bg-primary/10',
    dotColor: 'bg-violet-500',
    label: 'Time Edited',
  },
  manual_delete: {
    icon: 'Trash2',
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    dotColor: 'bg-red-500',
    label: 'Time Deleted',
  },
  overtime_alert: {
    icon: 'AlertTriangle',
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-100 dark:bg-amber-900/30',
    dotColor: 'bg-amber-500',
    label: 'Overtime Alert',
  },
  geofence_violation: {
    icon: 'MapPinOff',
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    dotColor: 'bg-rose-500',
    label: 'Location Violation',
  },
  shift_auto_close: {
    icon: 'Clock',
    color: 'text-gray-600 dark:text-gray-400',
    bgColor: 'bg-gray-100 dark:bg-gray-900/30',
    dotColor: 'bg-gray-500',
    label: 'Shift Auto-Closed',
  },
  shift_started: {
    icon: 'PlayCircle',
    color: 'text-indigo-600 dark:text-indigo-400',
    bgColor: 'bg-indigo-100 dark:bg-indigo-900/30',
    dotColor: 'bg-indigo-500',
    label: 'Shift Started',
  },
  shift_ended: {
    icon: 'StopCircle',
    color: 'text-slate-600 dark:text-slate-400',
    bgColor: 'bg-slate-100 dark:bg-slate-900/30',
    dotColor: 'bg-slate-500',
    label: 'Shift Ended',
  },
  employee_no_show: {
    icon: 'UserX',
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    dotColor: 'bg-red-600',
    label: 'No Show',
  },
  unassigned_shift_started: {
    icon: 'Calendar',
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
    dotColor: 'bg-purple-500',
    label: 'Unassigned Shift Started',
  },
  unassigned_shift_ended: {
    icon: 'CalendarOff',
    color: 'text-purple-500 dark:text-purple-300',
    bgColor: 'bg-purple-50 dark:bg-purple-900/20',
    dotColor: 'bg-purple-400',
    label: 'Unassigned Shift Ended',
  },
};
